import ko from "knockout";
import _ from "lodash";
import Promise from "bluebird";
import loggingService from "services/loggingService";
import userService from "services/userService";
import userSettingsService from "services/userSettingsService";
import ViewModel from "viewModels/ViewModel";

//
//Constants
//
const VOLUME_INCREMENT = 20;
const DEFAULT_VOLUME_LEVEL = 80;
const SETTINGS_NAMESPACE = "AudioSettings";

const volumeLevel = ko.observable(null);
ko.computed(function () {
    // Clear the cached level if the user changes
    ko.unwrap(userService.currentUser);
    volumeLevel(null);
});

// All volume controls share a common volume level, and it gets
// persisted to userSettingsService.
const userVolumeSettingService = {
    getVolume: function () {
        if (userService.currentUser()) {
            const unwrapped = ko.unwrap(volumeLevel);
            if (unwrapped) {
                return Promise.resolve(unwrapped);
            } else {
                return userSettingsService
                    .getSettings(SETTINGS_NAMESPACE)
                    .then(function (settings) {
                        const volumeFromSettings =
                            settings[SETTINGS_NAMESPACE].volume;
                        volumeLevel(volumeFromSettings || DEFAULT_VOLUME_LEVEL);
                        return volumeLevel();
                    })
                    .catch(function (error) {
                        loggingService.warn(
                            "There was an error getting user settings for volume:" +
                                error
                        );
                        return DEFAULT_VOLUME_LEVEL;
                    });
            }
        } else {
            return Promise.resolve(DEFAULT_VOLUME_LEVEL);
        }
    },
    setVolume: function (newValue) {
        volumeLevel(newValue);

        const newSettings = {};
        newSettings[SETTINGS_NAMESPACE] = {
            volume: newValue,
        };

        return userSettingsService.setSettings(newSettings).catch(function (e) {
            loggingService.warn("Failed to set the user's volume setting", e);
        });
    },
};

const VolumeControlView = ViewModel.extend(function (options) {
    const self = this;
    self.volumePopOverOpen = ko.observable(false);

    options = _.assign(
        {
            // volumeControls with 'syncVolumeToUserSettings = true' all share the same volumeLevel
            // and persist changes to userSettingsService.
            // volumeControls with 'syncVolumeToUserSettings = false' will use userSettingsService
            // to provide the default volume level only.
            syncVolumeToUserSettings: true,
        },
        options
    );

    if (options && options.syncVolumeToUserSettings) {
        self.volumeLevel = ko.computed({
            write: function (newValue) {
                volumeLevel(newValue);
                return newValue;
            },
            read: function () {
                const unwrapped = volumeLevel();
                if (null === unwrapped) {
                    return DEFAULT_VOLUME_LEVEL;
                }
                return unwrapped;
            },
        });
    } else {
        self.volumeLevel = ko.observable(DEFAULT_VOLUME_LEVEL);
    }

    self.volumeSliderText = ko.i18n.renderString("playback.volume.slider");

    self.updateVolume = function (percentPosition, event) {
        if (event) {
            event.preventDefault();
        }
        self.setVolumeLevel(percentPosition * 100);
    };

    const getVolumeLevel = function () {
        return parseInt(self.volumeLevel());
    };

    // An internal function for setting volume. This will not attempt
    // to update the volume level in userSettingsService.
    const setVolumeLevel = function (newValue) {
        if (newValue < 0) {
            newValue = 0;
        } else if (newValue > 100) {
            newValue = 100;
        }

        self.volumeLevel(newValue);
        return newValue;
    };

    /**
     * Sets the volume level from user settings
     */
    self.setDefaultVolume = function () {
        return userVolumeSettingService
            .getVolume()
            .then(function (volume) {
                return setVolumeLevel(volume);
            })
            .catch(function (e) {
                loggingService.warn("Error updating the volume level " + e);
                return DEFAULT_VOLUME_LEVEL;
            });
    };

    /**
     * Sets the volume level in the UI and also in the user settings.
     * @param volumeLevel
     */
    self.setVolumeLevel = function (newValue) {
        const temp = setVolumeLevel(newValue);
        if (options.syncVolumeToUserSettings) {
            userVolumeSettingService.setVolume(temp);
        }
    };

    self.increaseVolume = function () {
        const newLevel = getVolumeLevel() + VOLUME_INCREMENT;
        self.setVolumeLevel(newLevel);
    };

    self.decreaseVolume = function () {
        const newLevel = getVolumeLevel() - VOLUME_INCREMENT;
        self.setVolumeLevel(newLevel);
    };

    self.isMuted = self.computed(function () {
        return getVolumeLevel() <= 0;
    });
});

export default VolumeControlView;
